import { useEffect, useMemo, useRef, useState } from 'react';
import { ImagePlus, Sparkles, SlidersHorizontal } from 'lucide-react';
import { useEditor } from '../../state/store';
import { ColorMode, TRANSPARENT } from '../../core/types';
import { Grid } from '../../core/Grid';
import {
  ConversionMode,
  ConversionOptions,
  DEFAULT_CONVERSION,
  SourceImage,
  convertImage,
  fitToCells,
  loadImageData,
} from '../../image/convert';
import {
  DEFAULT_SMART,
  DEFAULT_SMART_FAMILIES,
  SmartOptions,
  smartConvert,
} from '../../image/smartConvert';
import {
  FAMILY_LABELS,
  GlyphFamily,
  buildGlyphAtlas,
  canvasRasterizer,
} from '../../image/glyphAtlas';
import { DitherMode } from '../../image/dither';
import { ASCII_RAMPS } from '../../unicode/charsets';
import { CellWrite } from '../../tools/types';
import { pickFile } from '../fileIO';
import { Checkbox, Field, Hint, Modal, NumberInput, Segmented } from '../widgets/common';
import { Select } from '../widgets/primitives';

const CLASSIC_MODES: ReadonlyArray<{ value: ConversionMode; label: string; note: string }> = [
  { value: 'halfblock', label: 'Half block', note: 'Two pixels per cell — best general choice' },
  { value: 'block', label: 'Block', note: 'One full block per cell' },
  { value: 'quarterblock', label: 'Quarter', note: 'Four pixels per cell' },
  { value: 'braille', label: 'Braille', note: '2×4 dots per cell, highest detail' },
  { value: 'ascii', label: 'ASCII', note: 'Luminance ramp, no colour' },
  { value: 'ascii-color', label: 'ASCII colour', note: 'Luminance ramp with colour' },
];

const DITHERS: ReadonlyArray<{ value: DitherMode; label: string }> = [
  { value: 'floyd-steinberg', label: 'Floyd–Steinberg' },
  { value: 'atkinson', label: 'Atkinson' },
  { value: 'ordered', label: 'Ordered' },
  { value: 'none', label: 'None' },
];

const ALL_FAMILIES: GlyphFamily[] = ['blocks', 'shades', 'box', 'braille', 'letters', 'punctuation'];

export function ImageImportDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const store = useEditor.getState();
  const tab = store.active();
  const [image, setImage] = useState<SourceImage | null>(null);
  const [fileName, setFileName] = useState('');
  const [engine, setEngine] = useState<'smart' | 'classic'>('smart');
  const [families, setFamilies] = useState<GlyphFamily[]>(DEFAULT_SMART_FAMILIES);
  const [smart, setSmart] = useState<SmartOptions>({
    ...DEFAULT_SMART,
    width: Math.min(80, tab.doc.width),
    height: Math.min(24, tab.doc.height),
    colorMode: tab.doc.colorMode,
  });
  const [classic, setClassic] = useState<ConversionOptions>({
    ...DEFAULT_CONVERSION,
    width: Math.min(80, tab.doc.width),
    height: Math.min(24, tab.doc.height),
    colorMode: tab.doc.colorMode,
  });
  const [asNewDocument, setAsNewDocument] = useState(true);
  const previewRef = useRef<HTMLPreElement>(null);

  /** The atlas only changes when the chosen families do; building it is the
   *  expensive part of smart conversion, so it is cached across renders. */
  const atlas = useMemo(
    () => buildGlyphAtlas({ families, rasterizer: canvasRasterizer() }),
    [families],
  );

  const choose = async () => {
    const file = await pickFile('image/png,image/jpeg,image/webp,image/gif,image/bmp');
    if (!file) return;
    try {
      const data = await loadImageData(file);
      setImage(data);
      setFileName(file.name);
      const targetWidth = Math.min(100, tab.doc.width);
      const fit = fitToCells(data.width, data.height, targetWidth, 'block');
      setSmart((s) => ({ ...s, width: targetWidth, height: fit.height }));
      setClassic((c) => ({ ...c, width: targetWidth, height: fit.height }));
    } catch (e) {
      store.notify(`Could not read the image: ${(e as Error).message}`, 'error');
    }
  };

  const result = useMemo(() => {
    if (!image) return null;
    if (engine === 'smart') {
      const { grid, usage } = smartConvert(image, atlas, smart);
      return { grid, usage };
    }
    const { grid } = convertImage(image, classic);
    return { grid, usage: null as Record<string, number> | null };
  }, [image, engine, atlas, smart, classic]);

  // Paint the preview as coloured spans.
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    el.textContent = '';
    if (!result) return;
    const grid: Grid = result.grid;
    for (let y = 0; y < Math.min(grid.height, 70); y++) {
      const line = document.createElement('div');
      let run: HTMLSpanElement | null = null;
      let runFg = -2;
      let runBg = -2;
      for (let x = 0; x < grid.width; x++) {
        const cell = grid.get(x, y);
        if (!run || cell.fg !== runFg || cell.bg !== runBg) {
          run = document.createElement('span');
          runFg = cell.fg;
          runBg = cell.bg;
          run.style.color = cell.fg === TRANSPARENT ? '#c8ccd4' : `#${cell.fg.toString(16).padStart(6, '0')}`;
          if (cell.bg !== TRANSPARENT) run.style.background = `#${cell.bg.toString(16).padStart(6, '0')}`;
          line.appendChild(run);
        }
        run.textContent += cell.char === '' ? ' ' : cell.char;
      }
      el.appendChild(line);
    }
  }, [result]);

  const insert = () => {
    if (!result) return;
    const grid = result.grid;
    if (asNewDocument) {
      store.newDocument({
        name: fileName.replace(/\.[^.]+$/, '') || 'image',
        width: grid.width,
        height: grid.height,
        colorMode: engine === 'smart' ? smart.colorMode : classic.colorMode,
        background: tab.doc.background,
      });
      const newTab = useEditor.getState().active();
      newTab.doc.layers[0].grid = grid;
      newTab.doc.layers[0].name = 'Image';
      useEditor.getState().bump();
    } else {
      const writes: CellWrite[] = [];
      for (let y = 0; y < grid.height; y++) {
        for (let x = 0; x < grid.width; x++) {
          const cell = grid.get(x, y);
          if (cell.char === '' && cell.bg === TRANSPARENT) continue;
          writes.push({ x, y, cell });
        }
      }
      store.writeCells(writes, 'Insert image');
    }
    store.notify(`Converted ${fileName} to ${grid.width}×${grid.height} cells.`, 'success');
    onClose();
  };

  const setSize = (width: number, height?: number) => {
    const h =
      height ??
      (image ? fitToCells(image.width, image.height, width, 'block').height : smart.height);
    setSmart((s) => ({ ...s, width, height: h }));
    setClassic((c) => ({ ...c, width, height: h }));
  };

  const size = engine === 'smart' ? { w: smart.width, h: smart.height } : { w: classic.width, h: classic.height };

  return (
    <Modal
      title="Image → ANSI"
      onClose={onClose}
      width={940}
      footer={
        <>
          <span className="faint mono" style={{ marginRight: 'auto' }}>
            {result ? `${result.grid.width}×${result.grid.height} cells` : 'no image'}
          </span>
          <Checkbox label="Create a new document" checked={asNewDocument} onChange={setAsNewDocument} />
          <button className="btn solid" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={!result} onClick={insert}>
            {asNewDocument ? 'Create document' : 'Insert into layer'}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '286px 1fr', gap: 16 }}>
        <div className="side-body" style={{ padding: 0, overflow: 'visible' }}>
          <button className="btn solid tall" style={{ width: '100%' }} onClick={choose}>
            <ImagePlus size={15} />
            {image ? `${fileName} (${image.width}×${image.height})` : 'Choose image…'}
          </button>

          <div className="segmented">
            <button className={engine === 'smart' ? 'active' : ''} onClick={() => setEngine('smart')}>
              <Sparkles size={13} /> Smart
            </button>
            <button className={engine === 'classic' ? 'active' : ''} onClick={() => setEngine('classic')}>
              <SlidersHorizontal size={13} /> Fixed mode
            </button>
          </div>

          <Hint>
            {engine === 'smart'
              ? 'Every cell is matched against the character library, so lines become box drawing, fine detail becomes Braille, and letters in the image come out as letters.'
              : 'One representation for the whole image.'}
          </Hint>

          <Field label="Width">
            <NumberInput value={size.w} min={1} max={500} onChange={(v) => setSize(v)} />
          </Field>
          <Field label="Height">
            <NumberInput
              value={size.h}
              min={1}
              max={500}
              onChange={(v) => setSize(size.w, v)}
            />
          </Field>
          <Field label="Colour">
            <Segmented
              value={engine === 'smart' ? smart.colorMode : classic.colorMode}
              options={[
                { value: 'ansi16' as ColorMode, label: '16' },
                { value: 'ansi256' as ColorMode, label: '256' },
                { value: 'truecolor' as ColorMode, label: 'True' },
              ]}
              onChange={(v) => {
                setSmart((s) => ({ ...s, colorMode: v }));
                setClassic((c) => ({ ...c, colorMode: v }));
              }}
            />
          </Field>

          {engine === 'smart' ? (
            <>
              <div className="group-title">Characters to use</div>
              {ALL_FAMILIES.map((f) => (
                <Checkbox
                  key={f}
                  label={FAMILY_LABELS[f]}
                  checked={families.includes(f)}
                  onChange={(on) =>
                    setFamilies((list) =>
                      on ? [...list, f] : list.filter((x) => x !== f),
                    )
                  }
                />
              ))}

              <Field label="Simplicity">
                <input
                  type="range"
                  min={0}
                  max={200}
                  value={Math.round(smart.simplicity * 100)}
                  style={{ width: '100%' }}
                  onChange={(e) => setSmart({ ...smart, simplicity: Number(e.target.value) / 100 })}
                />
              </Field>
              <Hint>
                Low favours the closest match even when it is a busy glyph; high favours plain blocks
                and shading.
              </Hint>

              <Field label="Brightness">
                <input
                  type="range"
                  min={-100}
                  max={100}
                  value={smart.brightness}
                  style={{ width: '100%' }}
                  onChange={(e) => setSmart({ ...smart, brightness: Number(e.target.value) })}
                />
              </Field>
              <Field label="Contrast">
                <input
                  type="range"
                  min={-100}
                  max={100}
                  value={smart.contrast}
                  style={{ width: '100%' }}
                  onChange={(e) => setSmart({ ...smart, contrast: Number(e.target.value) })}
                />
              </Field>
              <Checkbox
                label="Paint cell backgrounds"
                checked={smart.paintBackground}
                onChange={(v) => setSmart({ ...smart, paintBackground: v })}
              />
              <Checkbox
                label="Invert"
                checked={smart.invert}
                onChange={(v) => setSmart({ ...smart, invert: v })}
              />

              {result?.usage ? (
                <>
                  <div className="group-title">What it used</div>
                  <div className="dim" style={{ fontSize: 11, lineHeight: 1.7 }}>
                    {Object.entries(result.usage)
                      .sort((a, b) => b[1] - a[1])
                      .map(([family, count]) => (
                        <div key={family} className="row">
                          <span style={{ width: 92 }}>{family}</span>
                          <span className="mono">{count}</span>
                          <span className="faint">
                            {Math.round(
                              (count /
                                Object.values(result.usage as Record<string, number>).reduce(
                                  (a, b) => a + b,
                                  0,
                                )) *
                                100,
                            )}
                            %
                          </span>
                        </div>
                      ))}
                  </div>
                </>
              ) : null}
            </>
          ) : (
            <>
              <Field label="Mode">
                <Select
                  ariaLabel="Mode"
                  value={classic.mode}
                  options={CLASSIC_MODES.map((m) => ({ value: m.value, label: m.label, hint: m.note }))}
                  onChange={(mode) => {
                    if (image) {
                      const fit = fitToCells(image.width, image.height, classic.width, mode);
                      setClassic({ ...classic, mode, height: fit.height });
                    } else {
                      setClassic({ ...classic, mode });
                    }
                  }}
                />
              </Field>
              <Field label="Dither">
                <Select
                  ariaLabel="Dither"
                  value={classic.dither}
                  options={DITHERS.map((d) => ({ value: d.value, label: d.label }))}
                  onChange={(dither) => setClassic({ ...classic, dither })}
                />
              </Field>
              <Field label="Brightness">
                <input
                  type="range"
                  min={-100}
                  max={100}
                  value={classic.brightness}
                  style={{ width: '100%' }}
                  onChange={(e) => setClassic({ ...classic, brightness: Number(e.target.value) })}
                />
              </Field>
              <Field label="Contrast">
                <input
                  type="range"
                  min={-100}
                  max={100}
                  value={classic.contrast}
                  style={{ width: '100%' }}
                  onChange={(e) => setClassic({ ...classic, contrast: Number(e.target.value) })}
                />
              </Field>
              <Field label="Saturation">
                <input
                  type="range"
                  min={-100}
                  max={100}
                  value={classic.saturation}
                  style={{ width: '100%' }}
                  onChange={(e) => setClassic({ ...classic, saturation: Number(e.target.value) })}
                />
              </Field>
              {(classic.mode === 'ascii' || classic.mode === 'ascii-color') && (
                <Field label="Ramp">
                  <Select
                    ariaLabel="Ramp"
                    value={Object.entries(ASCII_RAMPS).find(([, r]) => r === classic.ramp)?.[0] ?? 'standard'}
                    options={Object.keys(ASCII_RAMPS).map((k) => ({ value: k, label: k }))}
                    onChange={(k) => setClassic({ ...classic, ramp: ASCII_RAMPS[k] ?? classic.ramp })}
                  />
                </Field>
              )}
              {classic.mode === 'braille' && (
                <Field label="Threshold">
                  <NumberInput
                    value={classic.threshold}
                    min={0}
                    max={255}
                    onChange={(v) => setClassic({ ...classic, threshold: v })}
                  />
                </Field>
              )}
              <Checkbox
                label="Invert"
                checked={classic.invert}
                onChange={(v) => setClassic({ ...classic, invert: v })}
              />
            </>
          )}
        </div>

        <div>
          <div className="group-title" style={{ marginBottom: 6 }}>
            Preview
          </div>
          <pre
            ref={previewRef}
            className="terminal-preview"
            style={{ maxHeight: '58vh', fontSize: 9, lineHeight: 1.06 }}
          />
          {!image ? (
            <div className="empty-state" style={{ marginTop: 10 }}>
              <ImagePlus size={20} />
              <span>Choose a PNG, JPEG, WEBP or GIF to begin.</span>
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
